import type { HTMLAttributes } from "react";
import { cx } from "./cx";
import "./StatusDot.css";

export type FlowStatus = "live" | "paused" | "dead" | "idle";

const DEFAULT_LABELS: Record<FlowStatus, string> = {
  live: "Live",
  paused: "Paused",
  dead: "Down",
  idle: "Idle",
};

export interface StatusDotProps extends HTMLAttributes<HTMLSpanElement> {
  status: FlowStatus;
  /** Visible word; defaults to Live / Paused / Down / Idle. Status is never colour alone. */
  label?: string;
  /** Muted trailing text, e.g. "beat 4 s ago". */
  meta?: string;
  /** Soft pulse ring; defaults to `status === "live"`. Off under reduced motion. */
  pulse?: boolean;
  /** Set when the value updates live (health page) → role="status". */
  live?: boolean;
}

/** Glyph per status so colour-blind / high-contrast users still read the shape. */
function Glyph({ status }: { status: FlowStatus }) {
  switch (status) {
    case "paused":
      return (
        <svg viewBox="0 0 10 10" className="ui-status__glyph" aria-hidden="true" focusable="false">
          <rect x="2" y="1.5" width="2" height="7" rx="0.5" />
          <rect x="6" y="1.5" width="2" height="7" rx="0.5" />
        </svg>
      );
    case "dead":
      return (
        <svg viewBox="0 0 10 10" className="ui-status__glyph" aria-hidden="true" focusable="false">
          <path d="M2 2l6 6M8 2l-6 6" className="ui-status__stroke" />
        </svg>
      );
    case "idle":
      return (
        <svg viewBox="0 0 10 10" className="ui-status__glyph" aria-hidden="true" focusable="false">
          <circle cx="5" cy="5" r="3.25" className="ui-status__stroke" />
        </svg>
      );
    default:
      return (
        <svg viewBox="0 0 10 10" className="ui-status__glyph" aria-hidden="true" focusable="false">
          <circle cx="5" cy="5" r="4" />
        </svg>
      );
  }
}

/** Shape- and colour-coded status glyph plus a word (and optional meta). */
export function StatusDot({
  status,
  label,
  meta,
  pulse,
  live = false,
  className,
  ...rest
}: StatusDotProps) {
  const pulsing = pulse ?? status === "live";
  return (
    <span
      className={cx("ui-status", `ui-status--${status}`, className)}
      data-status={status}
      role={live ? "status" : undefined}
      {...rest}
    >
      <span className={cx("ui-status__dot", pulsing && "ui-status__dot--pulse")}>
        <Glyph status={status} />
      </span>
      <span className="ui-status__label">{label ?? DEFAULT_LABELS[status]}</span>
      {meta ? <span className="ui-status__meta">{meta}</span> : null}
    </span>
  );
}
